import React, { useState } from 'react';
import { History, Copy, Eye, EyeOff, X, Check } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения истории сгенерированных паролей
 */
const PasswordHistory = ({ passwordHistory, clearPasswordHistory }) => {
  const [showPasswords, setShowPasswords] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  
  if (!passwordHistory || passwordHistory.length === 0) {
    return null;
  }
  
  // Копирование пароля в буфер обмена
  const copyToClipboard = (password, id) => {
    navigator.clipboard.writeText(password).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      alert('Не удалось скопировать пароль. Попробуйте выделить его вручную.');
    });
  };
  
  // Переключение видимости пароля
  const togglePasswordVisibility = (id) => {
    setShowPasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // Форматирование даты создания
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Получение типа пароля на русском
  const getPasswordTypeLabel = (type) => {
    const types = {
      'random': 'Случайный',
      'memorable': 'Запоминаемый',
      'pin': 'PIN-код',
      'phrase': 'Парольная фраза'
    };
    return types[type] || 'Пароль';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <History className="w-5 h-5 mr-2 text-violet-500" />
          История паролей
        </h3>
        <Button 
          size="sm"
          variant="outline"
          onClick={clearPasswordHistory}
          icon={<X className="w-4 h-4" />}
        >
          Очистить историю
        </Button>
      </div>
      
      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Тип
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Пароль
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Дата создания
              </th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {passwordHistory.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                  {getPasswordTypeLabel(item.type)}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                  {showPasswords[item.id] ? item.password : '••••••••••••'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(item.date)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => togglePasswordVisibility(item.id)}
                      className="text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-300 p-1 rounded-md"
                      aria-label={showPasswords[item.id] ? "Скрыть пароль" : "Показать пароль"}
                    >
                      {showPasswords[item.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => copyToClipboard(item.password, item.id)}
                      className="text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-300 p-1 rounded-md"
                      aria-label="Копировать пароль"
                    >
                      {copiedId === item.id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        История хранится только в вашем браузере и не отправляется на сервер.
      </p>
    </div>
  );
};

export default PasswordHistory;